import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { FileUploadResponse } from '../../types';
import apiService from '../../services/api';
import UploadStorageUsage from '../../components/UploadStorageUsage';
import Toast from '../../components/Toast';
import { getErrorMessage } from '../../utils/errorMessages';

interface UploadedResumeStatus {
  id: string;
  filename: string;
  uploadedAt: Date;
  status: 'uploaded' | 'processing' | 'queued' | 'completed' | 'failed' | 'duplicate' | 'retrying';
  resumeId?: string;
  candidateName?: string;
  candidateEmail?: string;
  candidatePhone?: string;
  errorMessage?: string;
  processingTime?: number;
  retryAttempts?: number;
}

const ResumeUpload: React.FC = () => {
  const { user } = useAuth();
  const [uploadedResumes, setUploadedResumes] = useState<UploadedResumeStatus[]>([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' | 'info'; isVisible: boolean }>({
    message: '',
    type: 'error',
    isVisible: false,
  });
  const [loading, setLoading] = useState(true);
  const [deletingResumeId, setDeletingResumeId] = useState<string | null>(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  
  // Add file upload state
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  const isCreditErrorMessage = (message: string) =>
    /insufficient credits|credits to view contact|required credits/i.test(message);

  const showToast = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'error') => {
    setToast({ message, type, isVisible: true });
  };

  const closeToast = () => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  };
  


  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    resumeId: string;
    filename: string;
  }>({
    isOpen: false,
    resumeId: '',
    filename: ''
  });

  // Ensure uploadedResumes is always an array
  const safeUploadedResumes = Array.isArray(uploadedResumes) ? uploadedResumes : [];

  // Safe setter that ensures array type
  const setSafeUploadedResumes = (value: UploadedResumeStatus[] | ((prev: UploadedResumeStatus[]) => UploadedResumeStatus[])) => {
    if (typeof value === 'function') {
      setUploadedResumes(prev => {
        const prevArray = Array.isArray(prev) ? prev : [];
        return value(prevArray);
      });
    } else {
      setUploadedResumes(Array.isArray(value) ? value : []);
    }
  };

  // Helper function to safely handle array operations
  const safeArrayMap = <T, R>(array: T[] | undefined | null, mapFn: (item: T, index: number) => R): R[] => {
    return Array.isArray(array) ? array.map(mapFn) : [];
  };

  // Helper function to safely update uploaded resumes
  const updateUploadedResumes = (updateFn: (prev: UploadedResumeStatus[]) => UploadedResumeStatus[]) => {
    setUploadedResumes(prev => {
      const prevArray = Array.isArray(prev) ? prev : [];
      return updateFn(prevArray);
    });
  };

  // Handle file selection and upload
  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    if (fileArray.length > 20) {
      setErrorMessage('Maximum 20 files allowed per upload');
      return;
    }

    setIsUploading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      let results: FileUploadResponse[];
      
      if (fileArray.length === 1) {
        // Single file upload
        const result = await apiService.uploadResume(fileArray[0]);
        results = [result];
      } else {
        // Bulk upload
        results = await apiService.bulkUploadResumes(fileArray);
      }

      handleUploadComplete(results, fileArray);
      
    } catch (error: any) {
      console.error('Upload error:', error);
      handleUploadError(getErrorMessage(error, 'Upload failed'));
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle button click to open file dialog
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };



  // Fetch recent resume uploads from database
  const fetchRecentUploads = async (search: string = '') => {
    try {
      setLoading(true);
      setErrorMessage(''); // Clear previous errors
      console.log('Fetching recent uploads...', search ? `with search: "${search}"` : '');
      
      // Get recent resumes from the API
      const response = await apiService.getRecentResumes(20, search);
      console.log('Recent uploads response:', response);
      
      // Handle case where response is not an array
      if (!Array.isArray(response)) {
        console.error('Expected array response, got:', typeof response, response);
        setErrorMessage('Invalid response format from server');
        return;
      }
      
      const recentResumes = response.map((resume: any) => {
        const mapped = {
          id: resume.id,
          filename: resume.original_filename,
          uploadedAt: new Date(resume.created_at),
          status: resume.status === 'processed' ? 'completed' as const :
                  resume.status === 'processing' ? 'processing' as const :
                  resume.status === 'uploaded' ? 'queued' as const :
                  resume.status === 'duplicate' ? 'duplicate' as const :
                  resume.status === 'error' || resume.status === 'failed' ? 'failed' as const : 'uploaded' as const,
          resumeId: resume.id,
          candidateName: resume.candidate ? `${resume.candidate.first_name} ${resume.candidate.last_name}` : undefined,
          candidateEmail: resume.candidate?.email,
          candidatePhone: resume.candidate?.phone || resume.candidate?.alternate_phone,
          errorMessage: resume.processing_error || (resume.status === 'duplicate' ? 'Duplicate resume detected' : undefined),
          processingTime: resume.processing_time
        };
        
        // Debug logging for failed resumes
        if (mapped.status === 'failed' || mapped.status === 'duplicate') {
          console.log('Failed/Duplicate resume mapping:', {
            originalResume: resume,
            mappedResume: mapped,
            hasResumeId: !!mapped.resumeId
          });
        }
        
        return mapped;
      });
      
      console.log('Mapped recent resumes:', recentResumes);
      setUploadedResumes(recentResumes);
      
      if (recentResumes.length === 0) {
        console.log('No recent uploads found, but API call succeeded');
      }
      
    } catch (error: any) {
      console.error('Failed to fetch recent uploads:', error);
      
      // Provide more specific error messages
      if (error.response) {
        const status = error.response.status;
        const detail = getErrorMessage(error, 'Unknown error');
        
        if (status === 401) {
          setErrorMessage('Authentication failed. Please log in again.');
        } else if (status === 403) {
          setErrorMessage('Access denied. You may not have permission to view uploads.');
        } else if (status === 404) {
          setErrorMessage('Upload data endpoint not found. Please contact support.');
        } else if (status === 500) {
          setErrorMessage(`Server error: ${detail}`);
        } else {
          setErrorMessage(`Failed to load uploads (${status}): ${detail}`);
        }
      } else if (error.request) {
        setErrorMessage('Network error. Please check your connection and try again.');
      } else {
        setErrorMessage(`Error: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Poll status for uploaded resumes
  const pollResumeStatus = async (resumeId: string, uploadId: string, maxAttempts: number = 300) => {
    let attempts = 0;
    
    const poll = async (): Promise<void> => {
      try {
        attempts++;
        console.log(`Polling resume ${resumeId} (attempt ${attempts}/${maxAttempts})`);
        
        const resume = await apiService.getResumeStatus(resumeId);
        
        // Map backend status to frontend status
        let frontendStatus: UploadedResumeStatus['status'];
        const statusString = String(resume.status);
        switch (statusString) {
          case 'processed':
            frontendStatus = 'completed';
            break;
          case 'processing':
            frontendStatus = 'processing';
            break;
          case 'error':
          case 'failed':
            frontendStatus = 'failed';
            break;
          default:
            frontendStatus = 'uploaded';
        }
        
        // Use functional update to prevent race conditions
        setUploadedResumes(prev => {
          // Ensure prev is an array to prevent .map() errors
          const prevArray = Array.isArray(prev) ? prev : [];
          return prevArray.map(upload => {
            // Only update the specific upload we're polling for
            if (upload.id === uploadId && upload.resumeId === resumeId) {
              return {
                ...upload, 
                status: frontendStatus,
                errorMessage: (resume as any).error || resume.error_message,
                processingTime: resume.processing_time,
                candidateName: (resume as any).candidate_name
              };
            }
            return upload;
          });
        });

        // If still processing and haven't exceeded max attempts, poll again
        if (statusString === 'processing' && attempts < maxAttempts) {
          setTimeout(poll, 2000); // Poll every 2 seconds
        } else if (statusString === 'processing' && attempts >= maxAttempts) {
          // Timeout - mark as failed
          console.warn(`Polling timeout for resume ${resumeId} after ${attempts} attempts`);
          setUploadedResumes(prev => {
            const prevArray = Array.isArray(prev) ? prev : [];
            return prevArray.map(upload => 
              upload.id === uploadId && upload.resumeId === resumeId
                ? { ...upload, status: 'failed', errorMessage: 'Processing timeout - please try again' }
                : upload
            );
          });
        } else {
          // Processing completed (success or failure)
          console.log(`Resume ${resumeId} processing completed with status: ${statusString}`);
        }
      } catch (error) {
        console.error('Failed to fetch resume status:', error);
        // Only update if we haven't exceeded max attempts
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000); // Retry after 5 seconds on error
        } else {
          setUploadedResumes(prev => prev.map(upload => 
            upload.id === uploadId && upload.resumeId === resumeId
              ? { ...upload, status: 'failed', errorMessage: 'Failed to fetch status' }
              : upload
          ));
        }
      }
    };
    
    // Start polling
    await poll();
  };

  // Fetch stats on component mount
  // Search handlers
  const handleSearch = async (search: string) => {
    setSearchTerm(search);
    await fetchRecentUploads(search);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Debounce search - trigger search after 500ms of no typing
    clearTimeout((window as any).searchTimeout);
    (window as any).searchTimeout = setTimeout(() => {
      fetchRecentUploads(value);
    }, 500);
  };

  const clearSearch = () => {
    setSearchTerm('');
    fetchRecentUploads('');
  };

  useEffect(() => {
    fetchRecentUploads();
  }, []);

  const handleUploadComplete = (results: FileUploadResponse[], uploadedFiles?: File[]) => {
    const timestamp = Date.now();
    const newUploads: UploadedResumeStatus[] = results.map((result, index) => {
      // Create a unique and stable ID - use resumeId if available, otherwise create unique ID
      const uploadId = result.resume_id ? `resume-${result.resume_id}` : `upload-${timestamp}-${index}`;
      
      return {
        id: uploadId,
        filename: uploadedFiles?.[index]?.name || `resume-${index + 1}.pdf`,
        uploadedAt: new Date(),
        status: result.status === 'processing' ? 'processing' : 
                result.status === 'completed' ? 'completed' :
                result.status === 'duplicate' ? 'duplicate' :
                result.status === 'failed' ? 'failed' : 'uploaded',
        resumeId: result.resume_id,
        errorMessage: (result.status === 'failed' || result.status === 'duplicate') ? result.message : undefined,
        processingTime: result.processing_time
      };
    });

    // Use functional update to prevent race conditions
    setUploadedResumes(prev => {
      // Remove any existing entries with the same resumeId to prevent duplicates
      const filteredPrev = prev.filter(upload => 
        !newUploads.some(newUpload => 
          newUpload.resumeId && upload.resumeId === newUpload.resumeId
        )
      );
      return [...newUploads, ...filteredPrev];
    });
    
    // Count successful vs failed uploads
    const successfulUploads = results.filter(r => r.status === 'processing' || r.status === 'completed').length;
    const duplicateUploads = results.filter(r => r.status === 'duplicate').length;
    const failedUploads = results.filter(r => r.status === 'failed').length;
    const creditErrorMessage = results.find((r) => {
      if ((r.status !== 'failed' && r.status !== 'duplicate') || typeof r.message !== 'string') {
        return false;
      }

      return isCreditErrorMessage(r.message);
    })?.message;

    if (creditErrorMessage) {
      showToast(creditErrorMessage, 'error');
    }
    
    // Show appropriate messages
    if (duplicateUploads > 0 && successfulUploads === 0 && !creditErrorMessage) {
      // All uploads were duplicates
      setErrorMessage(`${duplicateUploads === 1 ? 'This resume has' : 'These resumes have'} already been uploaded. Please upload different resumes.`);
      setSuccessMessage('');
    } else if (failedUploads > 0 && successfulUploads === 0 && !creditErrorMessage) {
      // All uploads failed
      setErrorMessage(`Failed to upload ${failedUploads} resume(s). Please try again.`);
      setSuccessMessage('');
    } else if (successfulUploads > 0) {
      // Some or all uploads succeeded
      let message = `Successfully uploaded ${successfulUploads} resume(s)!`;
      if (duplicateUploads > 0) {
        message += ` ${duplicateUploads} duplicate(s) were skipped.`;
      }
      if (failedUploads > 0) {
        message += ` ${failedUploads} upload(s) failed.`;
      }
      setSuccessMessage(message);
      setErrorMessage('');
    }
    
    // Start polling for each uploaded resume that's still processing
    newUploads.forEach((upload, index) => {
      if (upload.resumeId && upload.status === 'processing') {
        // Add a small delay to prevent race conditions, stagger multiple uploads
        setTimeout(() => {
          pollResumeStatus(upload.resumeId!, upload.id);
        }, 500 + (index * 100)); // Stagger by 100ms per upload
      }
    });
    
    // Clear success message after 5 seconds
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const handleUploadError = (error: string) => {
    if (isCreditErrorMessage(error)) {
      showToast(error, 'error');
      setErrorMessage('');
      setSuccessMessage('');
      return;
    }

    setErrorMessage(error);
    setSuccessMessage('');
  };

  const clearResults = () => {
    setUploadedResumes([]);
    setSuccessMessage('');
    setErrorMessage('');
  };

  const getStatusBadge = (status: UploadedResumeStatus['status']) => {
    switch (status) {
      case 'uploaded':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Uploaded
          </span>
        );
      case 'queued':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            Queued
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <svg className="animate-spin w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H8a1 1 0 010 2H4a1 1 0 01-1-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H12a1 1 0 110-2h4a1 1 0 011 1v4a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
            </svg>
            Processing
          </span>
        );
      case 'retrying':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
            <svg className="animate-spin w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H8a1 1 0 010 2H4a1 1 0 01-1-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H12a1 1 0 110-2h4a1 1 0 011 1v4a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
            </svg>
            Retrying
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Completed
          </span>
        );
      case 'duplicate':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
            Duplicate
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Failed
          </span>
        );
      default:
        return null;
    }
  };

  const handleReprocessResume = async (resumeId: string) => {
    try {
      // Find the current upload to check retry attempts
      const currentUpload = uploadedResumes.find(upload => upload.resumeId === resumeId);
      const currentAttempts = currentUpload?.retryAttempts || 0;
      
      if (currentAttempts >= 2) {
        setErrorMessage('Maximum retry attempts reached. Please contact TalentKey.AI support for assistance.');
        return;
      }
      
      // First set status to retrying to show immediate feedback
      setUploadedResumes(prev => prev.map(upload => 
        upload.resumeId === resumeId ? { 
          ...upload, 
          status: 'retrying',
          retryAttempts: (upload.retryAttempts || 0) + 1,
          errorMessage: undefined // Clear previous error message
        } : upload
      ));
      
      await apiService.reprocessResume(resumeId);
      
      // Update to processing status after API call succeeds
      setUploadedResumes(prev => prev.map(upload => 
        upload.resumeId === resumeId ? { 
          ...upload, 
          status: 'processing'
        } : upload
      ));
      
      // Start polling for the retried resume
      const uploadId = currentUpload?.id;
      if (uploadId) {
        pollResumeStatus(resumeId, uploadId);
      }
      
      setSuccessMessage(`Resume processing restarted (Attempt ${currentAttempts + 1}/2)`);
      setErrorMessage('');
    } catch (error) {
      console.error('Failed to reprocess resume:', error);
      setErrorMessage('Failed to reprocess resume');
      
      // Revert status back to failed if API call fails
      setUploadedResumes(prev => prev.map(upload => 
        upload.resumeId === resumeId ? { 
          ...upload, 
          status: 'failed',
          retryAttempts: (upload.retryAttempts || 0) // Don't increment if API call failed
        } : upload
      ));
    }
  };

  const handleDeleteResume = async (resumeId: string, filename?: string) => {
    // Open custom confirmation modal
    console.log('Opening delete modal for:', resumeId, filename);
    setDeleteModal({
      isOpen: true,
      resumeId,
      filename: filename || 'Unknown file'
    });
  };

  const confirmDeleteResume = async () => {
    const { resumeId } = deleteModal;
    
    // Close modal and start deletion
    setDeleteModal({ isOpen: false, resumeId: '', filename: '' });
    setDeletingResumeId(resumeId);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // Call delete with auto_cleanup=true (default)
      const response = await apiService.deleteResume(resumeId);
      
      // Remove from local state
      setUploadedResumes(prev => prev.filter(upload => upload.resumeId !== resumeId));
      
      // Show enhanced success message with cleanup details
      const deletedItems = response.deleted_items || {};
      const cleanupResults = response.cleanup_results || {};
      
      const deletedCount = Object.values(deletedItems).reduce((sum: number, value: any) => {
        if (typeof value === 'number') return sum + value;
        if (typeof value === 'boolean' && value) return sum + 1;
        return sum;
      }, 0);
      
      let successMsg = `Resume deleted successfully! Removed ${deletedCount} related items.`;
      
      if (cleanupResults.candidates > 0) {
        successMsg += ` Also cleaned up ${cleanupResults.candidates} orphaned candidates.`;
      }
      
      setSuccessMessage(successMsg);
      
      // Clear success message after 8 seconds
      setTimeout(() => setSuccessMessage(''), 8000);
      
    } catch (error: any) {
      console.error('Failed to delete resume:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to delete resume';
      setErrorMessage(`Delete failed: ${errorMsg}`);
      
      // Clear error message after 10 seconds
      setTimeout(() => setErrorMessage(''), 10000);
    } finally {
      setDeletingResumeId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Resume Upload</h1>
          <p className="mt-2 text-lg text-gray-600">
            Upload candidate resumes for AI-powered parsing and analysis
          </p>
        </div>

        {/* Status Messages */}
        {successMessage && (
          <div className="mb-8 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{errorMessage}</p>
              </div>
            </div>
          </div>
        )}

        {toast.isVisible && (
          <Toast
            message={toast.message}
            type={toast.type}
            isVisible={toast.isVisible}
            onClose={closeToast}
          />
        )}

        {/* Upload Component */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            
            <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Resumes</h3>
            <p className="text-sm text-gray-500 mb-6">
              Select up to 20 resume files to upload and process
            </p>
            
            <button
              onClick={handleUploadClick}
              disabled={isUploading}
              className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white transition-colors ${
                isUploading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
              }`}
            >
              {isUploading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Choose Files
                </>
              )}
            </button>
            
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.txt"
              onChange={(e) => handleFileUpload(e.target.files)}
              className="hidden"
            />
            
            <div className="mt-4 text-xs text-gray-500 space-y-1">
              <p>• Supported formats: PDF, DOC, DOCX, TXT</p>
              <p>• Maximum file size: 10MB per file</p>
              <p>• Maximum 20 files per upload</p>
              <p>• Processing typically takes 30-60 seconds per resume</p>
            </div>
          </div>
        </div>

        {/* Storage Usage Section */}
        <div className="mb-8">
          <UploadStorageUsage />
        </div>

        {/* Upload Status Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Upload Status
              </h3>
              <button
                onClick={() => {
                  fetchRecentUploads(searchTerm);
                }}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
            
            {/* Search Input */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchInputChange}
                  placeholder="Search by filename, name, email, phone..."
                  className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                {searchTerm && (
                  <button
                    onClick={clearSearch}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              {searchTerm && (
                <div className="mt-1 text-xs text-gray-500">
                  Tip: Use commas to search multiple terms (e.g., "john, smith, developer")
                </div>
              )}
            </div>

            {/* Upload Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                        <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 001 1h6a1 1 0 001-1V3a2 2 0 012 2v6.5a1.5 1.5 0 01-1.5 1.5h-7A1.5 1.5 0 016 11.5V5z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total Uploads</p>
                    <p className="text-2xl font-semibold text-gray-900">{safeUploadedResumes.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Successful</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {safeUploadedResumes.filter(r => r.status === 'completed').length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Processing</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {safeUploadedResumes.filter(r => ['processing', 'queued', 'uploaded', 'retrying'].includes(r.status)).length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Failed</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {safeUploadedResumes.filter(r => ['failed', 'duplicate'].includes(r.status)).length}
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    FILE NAME
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    STATUS
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    PROGRESS
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    TIME
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <svg className="animate-spin w-8 h-8 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p>Loading recent uploads...</p>
                      </div>
                    </td>
                  </tr>
                ) : uploadedResumes.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <svg className="w-12 h-12 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p>No uploads yet</p>
                        <p className="text-sm text-gray-400">Upload resumes to see their processing status</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  uploadedResumes.map((upload) => (
                    <tr key={upload.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 bg-gray-100 rounded-lg flex items-center justify-center">
                              <svg className="h-4 w-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
                              </svg>
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {upload.filename}
                            </div>
                            {upload.candidateName && (
                              <div className="text-sm text-gray-500">
                                {upload.candidateName}
                              </div>
                            )}
                            {upload.candidateEmail && (
                              <div className="text-xs text-gray-400">
                                {upload.candidateEmail}
                              </div>
                            )}
                            {upload.candidatePhone && (
                              <div className="text-xs text-gray-400">
                                {upload.candidatePhone}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap w-32">
                        {getStatusBadge(upload.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap w-24">
                        <div className="flex items-center">
                          <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                            <div 
                              className={`h-2 rounded-full ${
                                upload.status === 'completed' ? 'bg-green-500' :
                                upload.status === 'processing' ? 'bg-yellow-500 animate-pulse' :
                                upload.status === 'retrying' ? 'bg-indigo-500 animate-pulse' :
                                upload.status === 'queued' ? 'bg-purple-500' :
                                upload.status === 'failed' ? 'bg-red-500' : 'bg-blue-500'
                              }`}
                              style={{ 
                                width: upload.status === 'completed' ? '100%' :
                                       upload.status === 'processing' ? '60%' :
                                       upload.status === 'retrying' ? '30%' :
                                       upload.status === 'queued' ? '25%' :
                                       upload.status === 'failed' ? '100%' : '10%'
                              }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {upload.status === 'completed' ? '100%' :
                             upload.status === 'processing' ? '60%' :
                             upload.status === 'retrying' ? 'Retrying' :
                             upload.status === 'queued' ? 'Queued' :
                             upload.status === 'failed' ? 'Failed' : '10%'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {upload.uploadedAt.toLocaleDateString()} {upload.uploadedAt.toLocaleTimeString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium w-24">
                        {upload.status === 'failed' && (
                          <>
                            {(upload.retryAttempts || 0) < 2 ? (
                              <button
                                onClick={() => {
                                  if (upload.resumeId) {
                                    handleReprocessResume(upload.resumeId);
                                  } else {
                                    setErrorMessage('Cannot retry: Resume ID not found');
                                  }
                                }}
                                className={`inline-flex items-center px-3 py-1.5 border text-xs font-medium rounded-lg transition-colors ${
                                  upload.resumeId 
                                    ? 'border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2' 
                                    : 'border-gray-300 text-gray-400 bg-gray-50 cursor-not-allowed'
                                }`}
                                title={upload.resumeId ? `Retry processing (${(upload.retryAttempts || 0) + 1}/2)` : "Resume ID not available"}
                                disabled={!upload.resumeId}
                              >
                                <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                Retry
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  window.open('mailto:support@talentkey.ai?subject=Resume Processing Issue&body=Resume: ' + upload.filename, '_blank');
                                }}
                                className="inline-flex items-center px-3 py-1.5 border border-orange-300 text-xs font-medium rounded-lg text-orange-700 bg-orange-50 hover:bg-orange-100 hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-colors"
                                title="Contact TalentKey.AI support"
                              >
                                <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                Contact Support
                              </button>
                            )}
                          </>
                        )}
                        {upload.resumeId && (
                          <button
                            onClick={() => handleDeleteResume(upload.resumeId!, upload.filename)}
                            disabled={deletingResumeId === upload.resumeId}
                            className={`inline-flex items-center px-3 py-1.5 border text-xs font-medium rounded-lg transition-colors ${
                              deletingResumeId === upload.resumeId
                                ? 'border-gray-300 text-gray-400 bg-gray-50 cursor-not-allowed'
                                : 'border-red-300 text-red-700 bg-red-50 hover:bg-red-100 hover:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
                            }`}
                            title={deletingResumeId === upload.resumeId ? "Deleting..." : "Delete this resume and all related data (includes automatic cleanup)"}
                          >
                            {deletingResumeId === upload.resumeId ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-1.5 h-3 w-3 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Deleting...
                              </>
                            ) : (
                              <>
                                <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                DELETE
                              </>
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Upload Tips */}
        <div className="bg-blue-50 rounded-lg p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Upload Tips</h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Supported formats: PDF, DOC, DOCX, TXT</li>
                  <li>Maximum file size: 10MB per file</li>
                  <li>Maximum 20 files per upload</li>
                  <li>Clear, well-formatted resumes work best</li>
                  <li>Processing typically takes 30-60 seconds per resume</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {deleteModal.isOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <h3 className="ml-3 text-lg font-semibold text-gray-900">Delete Resume</h3>
                </div>
                <button
                  onClick={() => setDeleteModal({ isOpen: false, resumeId: '', filename: '' })}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to delete this resume?
                </p>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-medium text-red-800 mb-2">This will permanently delete:</h4>
                  <ul className="text-sm text-red-700 space-y-1">
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">•</span>
                      <span><strong>Resume file:</strong> {deleteModal.filename}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">•</span>
                      <span>All extracted data (work experience, education, skills)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">•</span>
                      <span>Any related processing records</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">•</span>
                      <span>Orphaned candidate profiles (automatic cleanup)</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <svg className="h-4 w-4 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm text-yellow-800">
                      <strong>Single-step process:</strong> This action combines deletion + cleanup in one step and cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button
                  onClick={() => setDeleteModal({ isOpen: false, resumeId: '', filename: '' })}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteResume}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  DELETE
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResumeUpload; 